import { useEffect, useRef, useState } from 'react';
import { Volume2 } from 'lucide-react';

/**
 * The 30-second promo (built by training/production/promo).
 *
 * Browsers only autoplay muted video, so it loops silently while on screen —
 * the on-screen text carries the message — and "Watch with sound" restarts it
 * from the top with the voiceover and normal controls. It never autoplays for
 * visitors who ask for reduced motion, and pauses when scrolled away.
 *
 * `base` is where the files live: this site's own /video, or scada.ink's for a
 * sister site that streams it from there rather than carrying its own copy.
 */
export function PromoVideo({ base = '/video' }: { base?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [withSound, setWithSound] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video || withSound) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.4 },
    );
    io.observe(video);
    return () => io.disconnect();
  }, [withSound]);

  const playWithSound = () => {
    const video = ref.current;
    if (!video) return;
    setWithSound(true);
    video.currentTime = 0;
    video.muted = false;
    video.loop = false;
    video.play().catch(() => {});
  };

  return (
    <div className="relative max-w-5xl mx-auto rounded-[24px] overflow-hidden border border-glass-border bg-white shadow-[0_40px_90px_-30px_rgba(30,41,59,0.35)]">
      <video
        ref={ref}
        className="block w-full aspect-video"
        poster={`${base}/scada-ink-promo-poster.jpg`}
        muted
        loop
        playsInline
        preload="metadata"
        controls={withSound}
        aria-label="Scada.Ink in 30 seconds: one appliance for SCADA, historian, analytics and MES, with no internet required"
      >
        <source src={`${base}/scada-ink-promo.webm`} type="video/webm" />
        <source src={`${base}/scada-ink-promo.mp4`} type="video/mp4" />
      </video>
      {!withSound && (
        <button
          type="button"
          onClick={playWithSound}
          className="absolute left-3 bottom-3 sm:left-6 sm:bottom-6 inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-navy-900/85 hover:bg-navy-900 backdrop-blur px-3 py-1.5 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg transition-colors"
        >
          <Volume2 size={14} />
          Watch with sound
        </button>
      )}
    </div>
  );
}
